import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Модальное окно вместо alert/confirm (инвариант 9).
 * Esc закрывает верхний слой, фокус уходит внутрь, фон под окном не скроллится.
 */
export function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/70 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className="w-full max-w-[440px] rounded-t-[10px] border border-line2 bg-panel pb-[env(safe-area-inset-bottom)] sm:rounded-[10px]"
      >
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="font-[family-name:var(--font-display)] text-[14px] text-txt">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={title}
            className="min-h-[var(--spacing-touch)] min-w-[var(--spacing-touch)] text-dim hover:text-txt"
          >
            &times;
          </button>
        </header>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}
